import React from 'react'
import type { BetError } from '@mobi/betslip/types'
import { layering } from '@mobi/component-library/Foundations'
import { toMoney } from '@mobi/utils/money'
import { keys } from '@classic/Foundation/Analytics/AnalyticsDataLayer'
import { trackEvent } from '@classic/Foundation/Analytics/GoogleTagManagerService'
import {
  ReceiptListItemStyled,
  ReceiptListItemTextStyled,
  ReceiptListRowStyled,
} from '@core/Areas/Quickbet/Components/Receipt/Components/Common/Receipt.styles'
import { BonusBetReceiptRowStyled } from '@core/Components/BettingDrawer/BettingDrawer.styles'
import { BettingDrawerBase } from '@core/Components/BettingDrawer/Components/BettingDrawerBase'
import { ButtonBlock } from '@core/Components/Buttons/ButtonBlock'
import { NoticeBox } from '@core/Components/NoticeBox'
import { Money } from '@core/Components/Text/Money'
import { state$ as userAccountState$ } from '@core/State/UserAccount/userAccountDriver'
import { useObservableImmutable } from '@core/Utils/hooks'
import { useLogon } from '@core/Utils/hooks/useLogon'
import { BonusCashCampaign } from './Components/Campaigns/BonusCashCampaign'

interface Props {
  open: boolean
  hideNoticeBox?: boolean
  totalCost: number
  accountBalance: number
  isConfirmDisabled?: boolean
  bonusBetBalance: number
  betError?: BetError
  onEditBet: () => void
  onConfirmBet: (useBonusCash: boolean) => void
  onDeposit: () => void
}

export const ConfirmBetDrawer: React.FC<Props> = ({
  open,
  hideNoticeBox,
  totalCost,
  accountBalance,
  isConfirmDisabled,
  bonusBetBalance,
  betError,
  onEditBet,
  onConfirmBet,
  onDeposit,
}) => {
  const { isLoggedIn } = useLogon()
  const hasBonusBet = bonusBetBalance > 0
  const bonusBetToUse = Math.min(bonusBetBalance, totalCost)
  const isInsufficientFunds = betError && betError.type === 'InsufficientFunds'
  const totalBalance = accountBalance + bonusBetBalance
  const isPartialPlacement = totalCost > totalBalance
  const warningText = isPartialPlacement
    ? 'Your account has insufficient funds, all bets cannot be placed.'
    : 'Bets cannot be cancelled once placed.'

  const [useBonusCash, setUseBonusCash] = React.useState(false)

  const { accountNumber, bonusCashBalance } = useObservableImmutable(userAccountState$, [
    'accountNumber',
    'bonusCashBalance',
  ])

  const calculateTotalCost = (): number => (useBonusCash ? totalCost - bonusBetToUse : totalCost)

  const handleBonusCashToggle = (isApplied: boolean) => {
    setUseBonusCash(isApplied)
    trackEvent(
      isApplied ? keys.mysteryBonusCashButtonDeactivated : keys.mysteryBonusCashButtonActivated,
      {
        accountNumber: accountNumber ?? '',
        accountBalance: accountBalance,
        bonusBetBalance: bonusBetBalance,
        bonusCashBalance: bonusCashBalance,
      }
    )
  }

  return (
    <BettingDrawerBase
      isOpen={open}
      forcedZIndex={layering.modalOverlay}
      noticeBoxSlot={
        !hideNoticeBox && (
          <>
            {hasBonusBet && (
              <BonusCashCampaign
                defaultValue={useBonusCash}
                toggleCampaign={handleBonusCashToggle}
              />
            )}

            <NoticeBox title='' noticeBoxType='Warning' hasBorder>
              {hasBonusBet && (
                <>
                  <ReceiptListRowStyled data-tid-notice-total-bet-cost>
                    <ReceiptListItemStyled>Total Bet Cost</ReceiptListItemStyled>
                    <ReceiptListItemTextStyled>
                      <Money amount={totalCost} />
                    </ReceiptListItemTextStyled>
                  </ReceiptListRowStyled>
                  {useBonusCash && (
                    <BonusBetReceiptRowStyled data-tid-notice-bonus-bet-amount-to-use>
                      <ReceiptListItemStyled>Bonus Cash</ReceiptListItemStyled>
                      <ReceiptListItemTextStyled>
                        <Money amount={-bonusBetToUse} />
                      </ReceiptListItemTextStyled>
                    </BonusBetReceiptRowStyled>
                  )}
                  <ReceiptListRowStyled data-tid-notice-amount-paying>
                    <ReceiptListItemStyled>Amount Paying</ReceiptListItemStyled>
                    <ReceiptListItemTextStyled>
                      <Money amount={calculateTotalCost()} />
                    </ReceiptListItemTextStyled>
                  </ReceiptListRowStyled>
                </>
              )}
              {!hasBonusBet && <strong>The total bet cost will be {toMoney(totalCost)}.</strong>}
              <br />
              <span>{warningText}</span>
            </NoticeBox>
          </>
        )
      }
      secondaryButtonSlot={
        <ButtonBlock
          type='button'
          color='secondary'
          onClick={onEditBet}
          data-tid-quickbet-edit-bet-btn
        >
          Edit your Bet
        </ButtonBlock>
      }
      primaryButtonSlot={
        isInsufficientFunds ? (
          <ButtonBlock type='button' color='primary' onClick={onDeposit}>
            Deposit
          </ButtonBlock>
        ) : (
          <ButtonBlock
            type='button'
            color='primary'
            disabled={!isLoggedIn || !!isConfirmDisabled}
            onClick={() => {
              onConfirmBet(useBonusCash)
            }}
            data-tid-quickbet-betting-btn
          >
            Confirm Bet
          </ButtonBlock>
        )
      }
    />
  )
}
